/*
 * NOTE
 * The shelter picks one of five date formats. The chosen value is stored on
 * `shelters.date_format` as a PHP format token, so the APP has to translate it
 * into every token dialect it renders with:
 *
 *   - moment      → date.helper.ts (display + parse lists)
 *   - PrimeVue    → <Calendar> `dateFormat` (v3.53.1: `yy` = 4-digit year)
 *   - date-fns    → @vuepic/vue-datepicker `format` prop
 *
 * These maps are static. The *active* format lives in `activeDateFormat.ts`.
 * */

type PhpDateFormat = 'm/d/Y' | 'd/m/Y' | 'Y-m-d' | 'd.m.Y' | 'd-m-Y';

const DEFAULT_PHP_DATE_FORMAT: PhpDateFormat = 'm/d/Y';

const PHP_DATE_FORMATS: readonly PhpDateFormat[] = ['m/d/Y', 'd/m/Y', 'Y-m-d', 'd.m.Y', 'd-m-Y'];

const PHP_TO_MOMENT_DATE_FORMAT: Record<PhpDateFormat, string> = {
  'm/d/Y': 'MM/DD/YYYY',
  'd/m/Y': 'DD/MM/YYYY',
  'Y-m-d': 'YYYY-MM-DD',
  'd.m.Y': 'DD.MM.YYYY',
  'd-m-Y': 'DD-MM-YYYY',
};

const PHP_TO_PRIMEVUE_DATE_FORMAT: Record<PhpDateFormat, string> = {
  'm/d/Y': 'mm/dd/yy',
  'd/m/Y': 'dd/mm/yy',
  'Y-m-d': 'yy-mm-dd',
  'd.m.Y': 'dd.mm.yy',
  'd-m-Y': 'dd-mm-yy',
};

// Month+year only, for month-view pickers. Month stays numeric and keeps the full
// format's separator and order. `Y-m-d` keeps year-first because that is its own order.
const PHP_TO_PRIMEVUE_MONTH_YEAR_FORMAT: Record<PhpDateFormat, string> = {
  'm/d/Y': 'mm/yy',
  'd/m/Y': 'mm/yy',
  'Y-m-d': 'yy-mm',
  'd.m.Y': 'mm.yy',
  'd-m-Y': 'mm-yy',
};

const PHP_TO_DATE_FNS_DATE_FORMAT: Record<PhpDateFormat, string> = {
  'm/d/Y': 'MM/dd/yyyy',
  'd/m/Y': 'dd/MM/yyyy',
  'Y-m-d': 'yyyy-MM-dd',
  'd.m.Y': 'dd.MM.yyyy',
  'd-m-Y': 'dd-MM-yyyy',
};

// Day+month only, for chart axis labels. Same separator and order as the full format,
// with the year dropped. `Y-m-d` keeps month-first because that is its own order.
const PHP_TO_MOMENT_DAY_MONTH_FORMAT: Record<PhpDateFormat, string> = {
  'm/d/Y': 'MM/DD',
  'd/m/Y': 'DD/MM',
  'Y-m-d': 'MM-DD',
  'd.m.Y': 'DD.MM',
  'd-m-Y': 'DD-MM',
};

const PHP_TO_DATE_FNS_DAY_MONTH_FORMAT: Record<PhpDateFormat, string> = {
  'm/d/Y': 'MM/dd',
  'd/m/Y': 'dd/MM',
  'Y-m-d': 'MM-dd',
  'd.m.Y': 'dd.MM',
  'd-m-Y': 'dd-MM',
};

// Legacy shapes still stored in Mongo and still emitted by the API's JSON layer
// (`m-d-Y` at ~30 response sites). They stay in every parse list forever.
const LEGACY_MOMENT_DATE_FORMATS = ['MM/DD/YYYY', 'MM-DD-YYYY'];

// The wire is always timezone-naive ISO (Model A) — the API anchors it to the
// shelter timezone. Never `.toISOString()` from a picker.
const PAYLOAD_DATE_FORMAT = 'YYYY-MM-DD';
const PAYLOAD_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export {
  DEFAULT_PHP_DATE_FORMAT,
  LEGACY_MOMENT_DATE_FORMATS,
  PAYLOAD_DATE_FORMAT,
  PAYLOAD_DATETIME_FORMAT,
  PHP_DATE_FORMATS,
  PHP_TO_DATE_FNS_DATE_FORMAT,
  PHP_TO_DATE_FNS_DAY_MONTH_FORMAT,
  PHP_TO_MOMENT_DATE_FORMAT,
  PHP_TO_MOMENT_DAY_MONTH_FORMAT,
  PHP_TO_PRIMEVUE_DATE_FORMAT,
  PHP_TO_PRIMEVUE_MONTH_YEAR_FORMAT,
};

export type { PhpDateFormat };
